import { useState } from 'react';

import useMutateConsistencyCheckMutations from '@hooks/consistencyCheck/useMutateConsistencyCheckMutations';
import useQueryConsistencyCheckDates from '@hooks/consistencyCheck/useQueryConsistencyCheckDates';
import useQueryConsistencyCheckFetchBranches from '@hooks/consistencyCheck/useQueryConsistencyCheckFetchBranches';
import useQueryConsistencyCheckTechnologies from '@hooks/consistencyCheck/useQueryConsistencyCheckTechnologies';
import useQueryConsistencyCheckVendors from '@hooks/consistencyCheck/useQueryConsistencyCheckVendors';

import { ICMMavenirVendor } from '@interfaces/cmModule/cmModule';
import { TCMDefaultDTO, TMavenirDTO } from '@interfaces/cmModule/dto/cmModuleDTO';
import { TDefaultDTO } from '@interfaces/common/defaultDTO';

import useConsistencyCheckGenerateBranches from './useConsistencyCheckGenerateBranches';
import useQueryConsistencyCheckConfigTypes from './useQueryConsistencyCheckConfigTypes';
import useQueryConsistencyCheckDatesOptions from './useQueryConsistencyCheckDatesOptions';
import useQueryConsistencyCheckNodes from './useQueryConsistencyCheckNodes';
import useQueryConsistencyCheckNodeTypes from './useQueryConsistencyCheckNodeTypes';
import useQueryConsistencyCheckVersions from './useQueryConsistencyCheckVersions';

type TExtendedTypes = TDefaultDTO & TMavenirDTO & ICMMavenirVendor & Omit<TCMDefaultDTO, 'siteIds'>

interface IUseConsistencyCheckBranchParams1 extends TExtendedTypes {
	configurationDate?: number;
}

const useConsistencyCheck = (params: IUseConsistencyCheckBranchParams1) => {
	const [isProgressRun, setIsProgressRun] = useState(false);

	const {
		configurationDate,
		nodeType,
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		technology,
		vendor,
		version,
		type,
		nodes,
		isMavenirVendor,
	} = params;

	const {
		generateBranches,
		isLoadingBranchesGenerated,
	} = useMutateConsistencyCheckMutations(setIsProgressRun);

	const { generateProgressId, progressData, progressId } = useConsistencyCheckGenerateBranches({
		configurationDate,
		isProgressRun,
		nodeType,
		technology,
		vendor,
		nodes,
		type,
		version,
		generateBranches,
		setIsProgressRun,
	});

	const { branchData, countOfRecords, isLoadingBranchData } = useQueryConsistencyCheckFetchBranches({
		page,
		pageSize,
		searchValue,
		sortDirection,
		sortFieldName,
		progress: progressData,
		progressId,
	});

	const { vendorsOptions, isLoadingVendorOptions } = useQueryConsistencyCheckVendors();

	const { technologiesOptions, isLoadingTechnologiesOptions } = useQueryConsistencyCheckTechnologies({
		vendor,
		isMavenirVendor,
	});

	const { nodesTypesOptions, isLoadingNodeTypesOptions } = useQueryConsistencyCheckNodeTypes({
		technology,
		vendor,
		isMavenirVendor,
	});

	const { nodeOptions, isLoadingNodeOptions } = useQueryConsistencyCheckNodes({
		vendor,
		isMavenirVendor,
	});

	const { versionOptions, isLoadingVersionOptions } = useQueryConsistencyCheckVersions({
		isMavenirVendor,
		nodes,
		vendor,
	});

	const { configTypeOptions, isLoadingConfigTypeOptions } = useQueryConsistencyCheckConfigTypes({
		isMavenirVendor,
		nodes,
		vendor,
		version,
	});

	const { datesOptions, isLoadingDatesOptions } = useQueryConsistencyCheckDatesOptions({
		vendor,
		type: type,
		version,
		nodes,
	});

	const { dates, isLoadingDates } = useQueryConsistencyCheckDates({
		nodeType,
		technology,
		vendor,
	});

	return {
		dumpId: progressData?.dumpId,
		branch: branchData,
		countOfRecords,
		datesOptions,
		dates,
		nodesTypesOptions,
		progressData,
		technologiesOptions,
		vendorsOptions,
		nodeOptions,
		versionOptions,
		configTypeOptions,
		generateProgressId,
		isLoadingBranchData: (
			isLoadingBranchData
			|| isLoadingBranchesGenerated
			|| isProgressRun
		),
		isLoadingOptions: (
			isLoadingDatesOptions
			|| isLoadingNodeTypesOptions
			|| isLoadingTechnologiesOptions
			|| isLoadingVendorOptions
			|| isLoadingNodeOptions
			|| isLoadingVersionOptions
			|| isLoadingConfigTypeOptions
			|| isLoadingDates
		),
	};

};

export default useConsistencyCheck;
